import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/auth";
import { fmtDateTime } from "@/lib/datetime";
import { Inbox, FolderOpen, ImageIcon, Sparkles, ArrowRight } from "lucide-react";
import PurgeDemoDataCard from "@/components/PurgeDemoDataCard";

type Stats = {
    contacts_total: number;
    contacts_new: number;
    projects_total: number;
    media_total: number;
    latest_contacts: Array<{
        id: string; name: string; email: string; object_type: string;
        location: string; status: string; created_at: string;
    }>;
};

export default function AdminDashboardPage() {
    const [stats, setStats] = useState<Stats | null>(null);
    const [error, setError] = useState("");

    useEffect(() => {
        void (async () => {
            const res = await api<Stats>("/api/admin/stats");
            if (res.ok && res.data) setStats(res.data);
            else setError(res.error || "Konnte Statistik nicht laden.");
        })();
    }, []);

    // eslint-disable-next-line react/no-unstable-nested-components
    const Tile = ({
        label, value, icon, testId, tone = "neutral",
    }: {
        label: string; value: number | string; icon: React.ReactNode; testId: string;
        tone?: "neutral" | "highlight";
    }) => (
        <div
            data-testid={testId}
            className="p-6 rounded-sm"
            style={{
                background: tone === "highlight" ? "hsl(var(--hf-gold-soft) / 0.5)" : "hsl(var(--hf-bg))",
                border: `1px solid ${tone === "highlight" ? "hsl(var(--hf-gold) / 0.5)" : "hsl(var(--hf-line))"}`,
            }}
        >
            <div className="flex items-center gap-3">
                <span
                    className="inline-flex items-center justify-center w-9 h-9 rounded-sm"
                    style={{
                        background: tone === "highlight" ? "hsl(var(--hf-gold))" : "hsl(var(--hf-surface))",
                        color: "hsl(var(--hf-ink))",
                    }}
                >
                    {icon}
                </span>
                <span className="hf-eyebrow">{label}</span>
            </div>
            <div className="mt-4 font-display text-4xl md:text-5xl text-hf-ink">
                {value}
            </div>
        </div>
    );

    return (
        <div data-testid="admin-dashboard">
            <div className="hf-eyebrow">Übersicht</div>
            <h1 className="font-display text-3xl md:text-4xl mt-2">Dashboard</h1>
            <p className="mt-2 text-hf-muted">
                Der aktuelle Stand deiner Plattform auf einen Blick.
            </p>

            {error && (
                <div
                    className="mt-6 p-3 rounded text-sm"
                    style={{
                        background: "hsl(var(--hf-danger) / 0.12)",
                        color: "hsl(var(--hf-danger))",
                        border: "1px solid hsl(var(--hf-danger) / 0.3)",
                    }}
                >
                    {error}
                </div>
            )}

            <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <Tile
                    testId="tile-contacts-new"
                    label="Neue Anfragen"
                    value={stats?.contacts_new ?? "—"}
                    icon={<Inbox size={16} />}
                    tone="highlight"
                />
                <Tile
                    testId="tile-contacts-total"
                    label="Anfragen gesamt"
                    value={stats?.contacts_total ?? "—"}
                    icon={<Inbox size={16} />}
                />
                <Tile
                    testId="tile-projects"
                    label="Projekte"
                    value={stats?.projects_total ?? "—"}
                    icon={<FolderOpen size={16} />}
                />
                <Tile
                    testId="tile-media"
                    label="Medien"
                    value={stats?.media_total ?? "—"}
                    icon={<ImageIcon size={16} />}
                />
            </div>

            <div
                className="mt-10 p-6 md:p-8 rounded-sm"
                style={{
                    background: "hsl(var(--hf-bg))",
                    border: "1px solid hsl(var(--hf-line))",
                }}
            >
                <div className="flex items-baseline justify-between">
                    <div>
                        <div className="hf-eyebrow">Neueste 5 Anfragen</div>
                        <div className="font-display text-2xl mt-1">Was gerade herein­kommt</div>
                    </div>
                    <Link
                        to="/admin/anfragen"
                        className="text-sm inline-flex items-center gap-1 text-hf-ink hover:text-hf-gold-dark transition-colors"
                        data-testid="dashboard-link-anfragen"
                    >
                        Alle Anfragen <ArrowRight size={14} />
                    </Link>
                </div>
                <div className="mt-5 divide-y" style={{ borderColor: "hsl(var(--hf-line))" }}>
                    {(stats?.latest_contacts || []).length === 0 && (
                        <div className="py-8 flex items-center gap-3 text-hf-muted">
                            <Sparkles size={16} style={{ color: "hsl(var(--hf-gold-dark))" }} />
                            <span>Noch keine Anfragen. Sobald das Formular Anfragen liefert, siehst du sie hier.</span>
                        </div>
                    )}
                    {(stats?.latest_contacts || []).map((c) => (
                        <Link
                            key={c.id}
                            to={`/admin/anfragen/${c.id}`}
                            data-testid={`dashboard-contact-${c.id}`}
                            className="py-4 flex items-center gap-4 hover:bg-hf-surface transition-colors -mx-4 md:-mx-6 px-4 md:px-6 rounded-sm"
                        >
                            <div className="flex-1 min-w-0">
                                <div className="font-medium text-hf-ink truncate">{c.name}</div>
                                <div className="text-xs text-hf-muted truncate">
                                    {c.email} · {c.object_type || "—"} · {c.location || "—"}
                                </div>
                            </div>
                            <span
                                className="text-[10px] tracking-[0.14em] uppercase px-2 py-1 rounded-sm shrink-0"
                                style={{
                                    background: c.status === "neu"
                                        ? "hsl(var(--hf-gold-soft) / 0.7)"
                                        : "hsl(var(--hf-surface))",
                                    color: "hsl(var(--hf-ink))",
                                }}
                            >
                                {c.status}
                            </span>
                            <div className="text-xs text-hf-muted shrink-0 hidden md:block">
                                {fmtDateTime(c.created_at)}
                            </div>
                        </Link>
                    ))}
                </div>
            </div>

            {/* Testdaten & Setup — nur solange Demo-Inhalte in der DB sind */}
            <PurgeDemoDataCard />
        </div>
    );
}
